import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import { Panel, SectionTitle, TactLoader, StatBadge } from "../components/Tactical";
import { ShieldAlert, Check, X } from "lucide-react";
import GlobalFeed from "../components/GlobalFeed";
import ReportWarningBanner from "../components/ReportWarningBanner";
import CtfBanner from "../components/CtfBanner";
import HallOfFame from "../components/HallOfFame";
// WCTourneyHero removed iter 80 — superseded by the WC 2026 hub
// (/wc2026) and the top-nav "WC 2026" link. Component file kept for
// reference but no longer mounted.

export default function Dashboard({ activeLeague }) {
  const { user } = useAuth();
  const { t } = useT();
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // Only admins need the standings snapshot (shown in the admin-only bottom grid).
        if (user?.role === "admin" && activeLeague?.id) {
          await api.get(`/leagues/${activeLeague.id}/standings`)
            .then((r) => alive && setStandings(r.data))
            .catch(() => {});
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [user, activeLeague?.id]);

  const pct = activeLeague ? Math.min(100, Math.round((activeLeague.player_count / activeLeague.capacity) * 100)) : 0;

  return (
    <div data-testid="dashboard-page" className="space-y-12">
      {user && user !== false && <ReportWarningBanner />}
      <CtfBanner />

      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative overflow-hidden border border-[#E4E4E7] dark:border-[#2A2A30]"
      >
        {/* Blurred stadium background (user-requested: premium football gaming feel) */}
        <div
          className="absolute inset-0 bg-cover bg-center scale-110 blur-[3px]"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1522778119026-d647f0596c20?auto=format&fit=crop&w=1920&q=75')",
          }}
          aria-hidden
        />
        {/* Dark gradient overlay for readability — top lightly tinted so the
            stadium stays visible, bottom near-black so text reads cleanly. */}
        <div
          className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/55 to-black/90"
          aria-hidden
        />
        {/* Subtle pitch texture stripes */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.08]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(90deg, rgba(0,255,135,0.35) 0, rgba(0,255,135,0.35) 2px, transparent 2px, transparent 80px)",
          }}
          aria-hidden
        />
        <div className="relative p-8 md:p-12 text-white">
          <div className="flex items-center gap-4 mb-6">
            <img
              src="/logo-256.png"
              alt="ePlay"
              data-testid="hero-logo"
              className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl shadow-[0_0_30px_rgba(0,255,135,0.25)] ring-1 ring-white/20"
              width={64}
              height={64}
            />
            <div>
              <div className="font-heading text-2xl sm:text-3xl uppercase tracking-wider leading-none">ePlay</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00FF87] mt-1">
                {t("dash.kicker")}
              </div>
            </div>
          </div>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl uppercase tracking-wide leading-[0.95] max-w-3xl drop-shadow-[0_4px_18px_rgba(0,0,0,0.6)]">
            {t("dash.heroLine1")}<br />
            <span className="text-[#00FF87] drop-shadow-[0_0_22px_rgba(0,255,135,0.4)]">{t("dash.heroLine2")}</span>
          </h1>
          <p className="mt-6 max-w-2xl text-white/75 font-body" data-testid="hero-intro">
            {t("dash.heroIntro")}
          </p>

          {/* Terms & Conditions / Squad Rules */}
          <TermsBlock />

          {activeLeague ? (
            <div className="mt-10 grid grid-cols-1 md:grid-cols-4 gap-4 max-w-3xl">
              <StatBadge label={t("dash.currentSeason")} value={activeLeague.name} testId="hero-season" highlight />
              <StatBadge label={t("dash.status")} value={activeLeague.status.toUpperCase()} testId="hero-status" />
              <StatBadge label={t("dash.players")} value={`${activeLeague.player_count}/${activeLeague.capacity}`} testId="hero-count" />
              <StatBadge label={t("dash.fill")} value={`${pct}%`} testId="hero-fill" />
            </div>
          ) : null}

          {activeLeague && activeLeague.status === "open" && (
            <div className="mt-6 max-w-3xl">
              <div className="h-2 bg-white/10 border border-white/15">
                <div
                  className="h-full bg-gradient-to-r from-[#7C3AED] via-[#7C3AED] to-[#00FF87] shadow-[0_0_12px_rgba(0,255,135,0.45)]"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.3em] text-white/70">
                {activeLeague.capacity - activeLeague.player_count} {t("dash.slotsRemaining")}
              </div>
            </div>
          )}

          <div className="mt-8 flex flex-wrap gap-3">
            {user === false && (
              <>
                <Link to="/register" data-testid="hero-register-btn" className="bg-[#7C3AED] text-white font-heading uppercase tracking-widest px-6 py-3 hover:bg-[#6D28D9] transition-colors shadow-[0_8px_24px_rgba(124,58,237,0.35)]">{t("dash.claimSlot")}</Link>
                <Link to="/login" data-testid="hero-login-btn" className="border border-white/30 text-white px-6 py-3 text-xs uppercase tracking-widest hover:border-[#00FF87] hover:text-[#00FF87] transition-colors font-body">{t("dash.playerSignIn")}</Link>
              </>
            )}
            {activeLeague && (
              <Link to={`/leagues/${activeLeague.id}`} data-testid="hero-fixtures-btn" className="border border-white/30 text-white px-6 py-3 text-xs uppercase tracking-widest hover:border-[#00FF87] hover:text-[#00FF87] transition-colors font-body">{t("dash.viewFixtures")}</Link>
            )}
            <Link to="/standings" data-testid="hero-standings-btn" className="border border-white/30 text-white px-6 py-3 text-xs uppercase tracking-widest hover:border-[#00FF87] hover:text-[#00FF87] transition-colors font-body">{t("dash.leaderboard")}</Link>
          </div>
        </div>
      </section>

      {/* Hall of Fame — top 5 across all leagues (moved above feeds) */}
      <HallOfFame limit={5} />

      {/* Admin-only bottom grid: pending games + top standings snapshot */}
      {user?.role === "admin" && (
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <SectionTitle kicker={t("dash.pendingKicker")} testId="pending-title">{t("dash.pendingTitle")}</SectionTitle>
            {loading ? <TactLoader /> : (
              <Panel testId="pending-admin" className="text-[#71717A] font-mono text-sm">
                {t("dash.pendingAdmin")}
              </Panel>
            )}
          </div>

          <div>
            <SectionTitle kicker={t("dash.topKicker")} testId="top-title">{t("dash.topTitle")}</SectionTitle>
            {!activeLeague ? (
              <Panel className="font-mono text-[#71717A] text-sm">{t("dash.noLeagueActive")}</Panel>
            ) : standings.length === 0 ? (
              <Panel className="font-mono text-[#71717A] text-sm">{t("dash.awaitingResult")}</Panel>
            ) : (
              <Panel className="p-0">
                <div className="p-4 font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A] border-b border-[#E4E4E7] dark:border-[#2A2A30]">{activeLeague.name}</div>
                <table className="w-full">
                  <tbody>
                    {standings.slice(0, 5).map((s) => (
                      <tr key={s.user_id} className={`border-b border-[#E4E4E7] dark:border-[#2A2A30] ${s.rank === 1 ? "bg-[#7C3AED]/10" : ""}`} data-testid={`top-row-${s.rank}`}>
                        <td className={`py-3 px-4 font-mono text-sm ${s.rank === 1 ? "text-[#7C3AED]" : "text-[#09090B] dark:text-[#F4F4F5]"}`}>#{s.rank}</td>
                        <td className={`py-3 px-4 font-heading uppercase ${s.rank === 1 ? "text-[#7C3AED]" : "text-[#09090B] dark:text-[#F4F4F5]"}`}>{s.username}</td>
                        <td className="py-3 px-4 text-right font-mono text-sm">{s.points}<span className="text-[#71717A] text-[10px] ml-1">PTS</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Panel>
            )}
          </div>
        </section>
      )}

      {/* Global feed — visible to everyone */}
      <GlobalFeed />
    </div>
  );
}

function TermsBlock() {
  const { t } = useT();
  return (
    <div
      data-testid="terms-block"
      className="mt-8 max-w-3xl relative overflow-hidden rounded-[3px] border border-white/10 bg-gradient-to-br from-[#14141A]/95 via-[#0E0E12]/95 to-[#09090B]/95 backdrop-blur-xl shadow-[0_24px_60px_-20px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.08)]"
    >
      {/* Corner purple→green gradient glow, FUT-style */}
      <span
        className="pointer-events-none absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#7C3AED]/25 blur-3xl"
        aria-hidden
      />
      <span
        className="pointer-events-none absolute -bottom-24 -left-24 w-64 h-64 rounded-full bg-[#00FF87]/15 blur-3xl"
        aria-hidden
      />
      {/* Left accent bar */}
      <span
        className="pointer-events-none absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-[#E11D48] via-[#7C3AED] to-[#00FF87]"
        aria-hidden
      />

      <div className="relative p-6 sm:p-8">
        <div className="flex items-center gap-2.5 mb-4">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#E11D48]/20 border border-[#E11D48]/40 shadow-[0_0_14px_rgba(225,29,72,0.35)]">
            <ShieldAlert className="w-3.5 h-3.5 text-[#FB7185]" aria-hidden />
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#FB7185]">
            {t("tc.kicker")}
          </span>
        </div>
        <h3 className="font-heading text-3xl sm:text-4xl uppercase tracking-wide text-white leading-[0.95] drop-shadow-[0_2px_12px_rgba(0,0,0,0.6)]">
          {t("tc.title")}
        </h3>
        <p className="text-base text-white/70 mt-3 font-body leading-relaxed">
          {t("tc.intro")}
        </p>

        <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2.5 font-body" role="list">
          <RuleItem allowed text={t("tc.rule1")} testId="rule-1" />
          <RuleItem allowed={false} text={t("tc.rule2")} testId="rule-2" />
          <RuleItem allowed text={t("tc.rule3")} testId="rule-3" />
          <RuleItem allowed={false} text={t("tc.rule4")} testId="rule-4" />
        </ul>

        <div className="mt-6 pt-5 border-t border-white/10 font-mono text-[10px] uppercase tracking-[0.28em] text-white/55">
          {t("tc.ack")}
        </div>
      </div>
    </div>
  );
}

function RuleItem({ allowed, text, testId }) {
  const Icon = allowed ? Check : X;
  const iconWrap = allowed
    ? "bg-[#00FF87]/18 border-[#00FF87]/45 text-[#00FF87] shadow-[0_0_14px_rgba(0,255,135,0.35)]"
    : "bg-[#E11D48]/18 border-[#E11D48]/45 text-[#FB7185] shadow-[0_0_14px_rgba(225,29,72,0.3)]";
  const chip = allowed
    ? "border-[#00FF87]/30 text-[#00FF87]"
    : "border-[#FB7185]/40 text-[#FB7185]";
  return (
    <li
      className="flex items-start gap-3 bg-white/[0.03] hover:bg-white/[0.06] transition-colors border border-white/10 rounded-[2px] px-3 py-3"
      data-testid={testId}
    >
      <span className={`mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-full border ${iconWrap} flex-shrink-0`}>
        <Icon className="w-3.5 h-3.5" strokeWidth={2.75} />
      </span>
      <div className="min-w-0 flex-1">
        <span
          className={`inline-block font-mono text-[9px] uppercase tracking-[0.3em] border px-1.5 py-0.5 mb-1 ${chip}`}
        >
          {allowed ? "ALLOWED" : "BANNED"}
        </span>
        <div className="text-white text-sm leading-snug">{text}</div>
      </div>
    </li>
  );
}
